/**
 * routeRegistry.js — Single Source of Truth for ALL route RBAC declarations.
 *
 * Every routable page declares its path, allowed roles, and guard behaviour
 * in ONE place. Both the Router (App.js) and the Sidebar (GlobalSidebar)
 * consume this registry, so it is **impossible** for them to drift.
 *
 * ROLE SEMANTICS:
 *   rolesAllowed: []             → PUBLIC (any authenticated user)
 *   rolesAllowed: ['ATLETA']     → only ATLETA
 *   rolesAllowed: null/undefined → PUBLIC (any authenticated user)
 *   superAdminOnly: true         → only super admin (ignores roles)
 *   capability: 'arena.x.read'  → requires capability OR super admin
 *
 * GUARD SEMANTICS:
 *   guardType: 'role'        → uses RequireRole (default)
 *   guardType: 'access'      → uses RequireAccess (role + capability + super)
 *   guardType: 'governance'  → uses GovernanceGuard (founder-only, shows 404)
 *   guardType: 'none'        → no guard (public within auth)
 *
 * @version 1.0.0
 * @date 2026-02-21
 */

// ──────────────────────────────────────────────────────────────────────
// ROLE CONSTANTS — canonical strings, always UPPERCASE
// ──────────────────────────────────────────────────────────────────────
export const R = {
  ATLETA: 'ATLETA',
  TREINADOR: 'TREINADOR',
  PREPARADOR_FISICO: 'PREPARADOR_FISICO',
  GESTOR: 'GESTOR',
  NUTRICIONISTA: 'NUTRICIONISTA',
  BIOMECANICO: 'BIOMECANICO',
  RECRUITER: 'RECRUITER',
  OPERATIONS_ADMIN: 'OPERATIONS_ADMIN',
  // ── Governance roles (script 271) ────────────────────────────────
  SUPER_ADMIN: 'super_admin',
  GOVERNANCE_MANAGER: 'governance_manager',
  GOVERNANCE_ANALYST: 'governance_analyst',
  GOVERNANCE_VIEWER: 'governance_viewer',
  SPORTS_MANAGER: 'sports_manager',
  SPORTS_COORDINATOR: 'sports_coordinator',
  SPORTS_VIEWER: 'sports_viewer',
  INSTITUTION_ADMIN: 'institution_admin',
  INSTITUTION_MANAGER: 'institution_manager',
  INSTITUTION_STAFF: 'institution_staff',
  ATHLETE_PROFILE: 'athlete_profile',
  COACH_PROFILE: 'coach_profile',
  STRENGTH_COACH_PROFILE: 'strength_coach_profile',
  RECRUITER_PROFILE: 'recruiter_profile',
  CONTENT_CREATOR: 'content_creator',
  ANALYTICS_VIEWER: 'analytics_viewer',
  API_INTEGRATOR: 'api_integrator',
};

/**
 * ROLE_MAP — translate any external/token role name → canonical R.* key.
 * Keeps one place to maintain if backend/token naming changes.
 * Keys are **lowercase** for case-insensitive lookup.
 */
export const ROLE_MAP = {
  // canonical (already correct)
  atleta: R.ATLETA,
  treinador: R.TREINADOR,
  preparador_fisico: R.PREPARADOR_FISICO,
  aux_prep_fisica: R.PREPARADOR_FISICO,
  gestor: R.GESTOR,
  recruiter: R.RECRUITER,
  // EN aliases (backend / token variations)
  athlete: R.ATLETA,
  coach: R.TREINADOR,
  strength_coach: R.PREPARADOR_FISICO,
  assistant_prep: R.PREPARADOR_FISICO,
  manager: R.GESTOR,
  club_admin: R.GESTOR,
  scout: R.RECRUITER,
  // Internal / Operations
  operations_admin: R.OPERATIONS_ADMIN,
  // Governance roles (script 271)
  super_admin: R.SUPER_ADMIN,
  system_root: R.SUPER_ADMIN,
  governance_manager: R.GOVERNANCE_MANAGER,
  governance_admin: R.GOVERNANCE_MANAGER,
  governance_analyst: R.GOVERNANCE_ANALYST,
  governance_viewer: R.GOVERNANCE_VIEWER,
  sports_manager: R.SPORTS_MANAGER,
  sports_admin: R.SPORTS_MANAGER,
  sports_coordinator: R.SPORTS_COORDINATOR,
  sports_viewer: R.SPORTS_VIEWER,
  institution_admin: R.INSTITUTION_ADMIN,
  institution_manager: R.INSTITUTION_MANAGER,
  institution_staff: R.INSTITUTION_STAFF,
  athlete_profile: R.ATHLETE_PROFILE,
  coach_profile: R.COACH_PROFILE,
  strength_coach_profile: R.STRENGTH_COACH_PROFILE,
  recruiter_profile: R.RECRUITER_PROFILE,
  content_creator: R.CONTENT_CREATOR,
  analytics_viewer: R.ANALYTICS_VIEWER,
  api_integrator: R.API_INTEGRATOR,
  // Specialist roles
  nutricionista: R.NUTRICIONISTA,
  nutritionist: R.NUTRICIONISTA,
  biomecanico: R.BIOMECANICO,
  biomechanics_specialist: R.BIOMECANICO,
};

/** Normalize any role string to canonical R.* key (returns null if unknown). */
export function normalizeRole(raw) {
  if (!raw) return null;
  const key = String(raw).trim().toLowerCase();
  return ROLE_MAP[key] || null;
}

/** Normalize an array of role strings. Unknown values are dropped. */
export function normalizeRoles(rawList) {
  if (!Array.isArray(rawList)) return [];
  return rawList.map(normalizeRole).filter(Boolean);
}

// Pre-built role groups
export const CENTRAL_ROLES = [R.ATLETA, R.TREINADOR, R.PREPARADOR_FISICO];
export const ANALYTICS_ROLES = [R.ATLETA, R.TREINADOR, R.PREPARADOR_FISICO, R.GESTOR];
export const VIDEO_ANALYSIS_ROLES = [R.TREINADOR, R.GESTOR, R.BIOMECANICO];
export const TACTICS_ROLES = [R.TREINADOR, R.GESTOR];
export const PLAYBOOK_ROLES = [R.TREINADOR];
export const LIBRARY_ROLES = [R.TREINADOR, R.PREPARADOR_FISICO, R.GESTOR];
export const STAFF_ROLES = [R.TREINADOR, R.PREPARADOR_FISICO, R.NUTRICIONISTA, R.BIOMECANICO];
export const PREP_ROLES = [R.PREPARADOR_FISICO];
export const NUTRITION_ROLES = [R.NUTRICIONISTA];
export const BIOMECHANICS_ROLES = [R.BIOMECANICO];

// ──────────────────────────────────────────────────────────────────────
// ROUTE REGISTRY
// ──────────────────────────────────────────────────────────────────────

/**
 * @typedef {Object} RouteEntry
 * @property {string}   path           - React Router path
 * @property {string[]} rolesAllowed   - Roles that may access ([] = public-auth)
 * @property {string}   [guardType]    - 'role' | 'access' | 'governance' | 'none'
 * @property {string}   [capability]   - Capability key for RequireAccess
 * @property {boolean}  [superAdminOnly]
 * @property {string}   [fallback]     - Redirect on deny (default: /central)
 * @property {string}   [navGroup]     - Sidebar group (omit to hide from nav)
 * @property {string}   [label]        - Display label for sidebar/nav
 */

export const ROUTE_REGISTRY = [
  // ═══════════════════════════════════════════
  // CENTRAL HUB
  // ═══════════════════════════════════════════
  { path: '/central',                rolesAllowed: CENTRAL_ROLES, guardType: 'none' },
  // ↑ CentralGate handles its own role redirect, no guard wrapper needed

  // ═══════════════════════════════════════════
  // HOME PAGES — VUKZ Assist (Performance OS v2)
  // ═══════════════════════════════════════════
  { path: '/athlete/home',           rolesAllowed: [R.ATLETA] },
  { path: '/coach/home',             rolesAllowed: [R.TREINADOR] },

  // ═══════════════════════════════════════════
  // ATLETA — Performance OS
  // ═══════════════════════════════════════════
  { path: '/my/performance',         rolesAllowed: CENTRAL_ROLES },
  { path: '/my/training/:sportId',   rolesAllowed: [R.ATLETA] },
  { path: '/my/training/:sportId/*', rolesAllowed: [R.ATLETA] },
  { path: '/my/wellness',            rolesAllowed: [R.ATLETA] },
  { path: '/my/results',             rolesAllowed: [R.ATLETA] },
  { path: '/athlete',                rolesAllowed: [R.ATLETA] },
  { path: '/athlete/*',              rolesAllowed: [R.ATLETA] },
  { path: '/athlete/training',       rolesAllowed: [R.ATLETA] },
  { path: '/athlete/training/:planId', rolesAllowed: [R.ATLETA] },

  // ═══════════════════════════════════════════
  // TREINADOR
  // ═══════════════════════════════════════════
  { path: '/coach',                  rolesAllowed: [R.TREINADOR] },
  { path: '/coach/athlete/:userId',  rolesAllowed: [R.TREINADOR, R.PREPARADOR_FISICO, R.GESTOR, R.BIOMECANICO] },
  { path: '/coach/*',                rolesAllowed: [R.TREINADOR] },
  { path: '/coach/training',         rolesAllowed: [R.TREINADOR] },
  { path: '/coach/training/*',       rolesAllowed: [R.TREINADOR] },
  { path: '/coach/groups',           rolesAllowed: [R.TREINADOR] },
  { path: '/coach/groups/:groupId',  rolesAllowed: [R.TREINADOR] },

  // ═══════════════════════════════════════════
  // PREPARADOR FÍSICO
  // ═══════════════════════════════════════════
  { path: '/prep/training',          rolesAllowed: PREP_ROLES },
  { path: '/prep/builder',           rolesAllowed: PREP_ROLES },
  { path: '/prep/groups',            rolesAllowed: PREP_ROLES },
  { path: '/prep/groups/:groupId',   rolesAllowed: PREP_ROLES },

  // ═══════════════════════════════════════════
  // TREINOS — Unified training module
  // ═══════════════════════════════════════════
  { path: '/treinos',       rolesAllowed: [R.TREINADOR, R.PREPARADOR_FISICO, R.ATLETA, R.GESTOR] },
  { path: '/treinos/novo',  rolesAllowed: [R.TREINADOR, R.PREPARADOR_FISICO] },
  { path: '/treinos/:id',   rolesAllowed: [R.TREINADOR, R.PREPARADOR_FISICO, R.ATLETA, R.GESTOR] },

  // ═══════════════════════════════════════════
  // STAFF — Shared (Coach + Prep)
  // ═══════════════════════════════════════════
  { path: '/staff/wellness',         rolesAllowed: STAFF_ROLES },

  // ═══════════════════════════════════════════
  // GESTOR
  // ═══════════════════════════════════════════
  { path: '/manager',                rolesAllowed: [R.GESTOR] },
  { path: '/manager/*',              rolesAllowed: [R.GESTOR] },
  { path: '/manager/staff-scopes',   rolesAllowed: [R.GESTOR] },
  { path: '/manager/setup',          rolesAllowed: [] },  // Auth-only: wizard creates GESTOR role (ADR-3)

  // ═══════════════════════════════════════════
  // RECRUITER
  // ═══════════════════════════════════════════
  { path: '/recruiter',              rolesAllowed: [R.RECRUITER], guardType: 'access', capability: 'arena.recruiter.read' },
  { path: '/recruiter/*',            rolesAllowed: [R.RECRUITER], guardType: 'access', capability: 'arena.recruiter.read' },

  // ═══════════════════════════════════════════
  // BIBLIOTECA DIGITAL
  // ═══════════════════════════════════════════
  { path: '/library',                              rolesAllowed: LIBRARY_ROLES },
  { path: '/library/search',                       rolesAllowed: LIBRARY_ROLES },
  { path: '/library/item/:itemId',                 rolesAllowed: LIBRARY_ROLES },
  { path: '/library/create',                       rolesAllowed: LIBRARY_ROLES },
  { path: '/library/protocols',                    rolesAllowed: LIBRARY_ROLES },
  { path: '/library/methodologies',                rolesAllowed: LIBRARY_ROLES },
  { path: '/library/methodologies/:methodologyId', rolesAllowed: LIBRARY_ROLES },

  // ═══════════════════════════════════════════
  // ANALYTICS & INTELLIGENCE
  // ═══════════════════════════════════════════
  { path: '/analytics',              rolesAllowed: ANALYTICS_ROLES },
  { path: '/analytics/*',            rolesAllowed: ANALYTICS_ROLES },
  { path: '/mind',                   rolesAllowed: ANALYTICS_ROLES },
  { path: '/stats/mind',             rolesAllowed: ANALYTICS_ROLES },

  // ═══════════════════════════════════════════
  // SWIM VISION & TACTICS
  // ═══════════════════════════════════════════
  { path: '/swim-vision',            rolesAllowed: VIDEO_ANALYSIS_ROLES },
  { path: '/swim-vision/dashboard',  rolesAllowed: VIDEO_ANALYSIS_ROLES },
  { path: '/swim-vision/race/:sessionId', rolesAllowed: VIDEO_ANALYSIS_ROLES },
  { path: '/analise-videos',         rolesAllowed: VIDEO_ANALYSIS_ROLES }, // legacy redirect
  { path: '/tactics/designer',       rolesAllowed: TACTICS_ROLES },
  { path: '/tactics/plays/:id',      rolesAllowed: TACTICS_ROLES },
  { path: '/playbook/designer',      rolesAllowed: PLAYBOOK_ROLES },
  { path: '/playbook/minhas-jogadas', rolesAllowed: PLAYBOOK_ROLES },

  // ═══════════════════════════════════════════
  // RESEARCH (capability-gated)
  // ═══════════════════════════════════════════
  { path: '/research',               rolesAllowed: [], guardType: 'access', capability: 'arena.researcher.read' },

  // ═══════════════════════════════════════════
  // GOVERNANCE (super admin only — shows 404 on deny)
  // ═══════════════════════════════════════════
  { path: '/governance/*',           rolesAllowed: [], guardType: 'governance', superAdminOnly: true },

  // ═══════════════════════════════════════════
  // PUBLIC-AUTH — Any logged-in user
  // ═══════════════════════════════════════════
  { path: '/diagnostic',             rolesAllowed: [], guardType: 'none' },
  { path: '/jogadores',              rolesAllowed: [], guardType: 'none' },
  { path: '/clubes',                 rolesAllowed: [], guardType: 'none' },
  { path: '/locais',                 rolesAllowed: [], guardType: 'none' },
  { path: '/locais/:venueId',        rolesAllowed: [], guardType: 'none' },
  { path: '/esportes',               rolesAllowed: [], guardType: 'none' },
  { path: '/futebol',                rolesAllowed: [], guardType: 'none' },
  { path: '/futebol/campeonato/:leagueId/:season', rolesAllowed: [], guardType: 'none' },
  { path: '/futebol/campeonato-antigo/:leagueId/:season', rolesAllowed: [], guardType: 'none' },
  { path: '/futebol/partida/:matchId', rolesAllowed: [], guardType: 'none' },
  { path: '/meus-campeonatos',       rolesAllowed: [], guardType: 'none' },
  { path: '/campeonatos',            rolesAllowed: [], guardType: 'none' },
  { path: '/campeonato/:championshipId', rolesAllowed: [], guardType: 'none' },
  { path: '/partidas-2025',          rolesAllowed: [], guardType: 'none' },
  { path: '/configuracoes',          rolesAllowed: [], guardType: 'none' },
  { path: '/configuracoes/privacidade', rolesAllowed: [], guardType: 'none' },
  { path: '/natacao',                rolesAllowed: [], guardType: 'none' },
  { path: '/natacao/*',              rolesAllowed: [], guardType: 'none' },
  { path: '/nadador/:id',            rolesAllowed: [], guardType: 'none' },
  { path: '/agenda',                 rolesAllowed: [], guardType: 'none' },
  { path: '/stats',                  rolesAllowed: [], guardType: 'none' },
  { path: '/stats/ranking/:eventId', rolesAllowed: [], guardType: 'none' },
  { path: '/sobre',                  rolesAllowed: [], guardType: 'none' },
  { path: '/privacidade',            rolesAllowed: [], guardType: 'none' },
  { path: '/termos',                 rolesAllowed: [], guardType: 'none' },
  { path: '/contato',                rolesAllowed: [], guardType: 'none' },

  // Detail pages (public-auth)
  { path: '/jogador/:id',            rolesAllowed: [], guardType: 'none' },
  { path: '/usuario/:userId',        rolesAllowed: [], guardType: 'none' },
  { path: '/clube/:id',              rolesAllowed: [], guardType: 'none' },
  { path: '/clube/natacao/:id',      rolesAllowed: [], guardType: 'none' },
  { path: '/clube/futebol/:id',      rolesAllowed: [], guardType: 'none' },
  { path: '/organizacao/:id',        rolesAllowed: [], guardType: 'none' },
  { path: '/servicos/editorial-engine', rolesAllowed: [], guardType: 'none' },

  { path: '/institutions/create',    rolesAllowed: [], guardType: 'access', superAdminOnly: true },
];

// ──────────────────────────────────────────────────────────────────────
// HELPERS — used by Router, Sidebar, and anywhere else
// ──────────────────────────────────────────────────────────────────────

/**
 * Determines if a user can access a route.
 *
 * Used by BOTH the Router guard AND the Sidebar filter — single source of truth.
 *
 * CRITICAL RULE (P0):
 *   • Authorization comes from **userRoles** (token / backend) ONLY.
 *   • activeRole is a UX filter — it can RESTRICT visibility but NEVER grant access.
 *   • If activeRole is set and NOT in rolesAllowed, the route is hidden/denied
 *     even if userRoles would otherwise match (UX narrowing).
 *
 * @param {RouteEntry} route
 * @param {Object} ctx
 * @param {string[]} ctx.userRoles      - All roles from RBAC / token (source of truth)
 * @param {string}   ctx.activeRole     - Currently selected profile (UX only)
 * @param {boolean}  ctx.isSuperAdmin
 * @param {Function} ctx.hasCapability  - (cap) => boolean
 * @returns {boolean}
 */
export function canAccess(route, { userRoles = [], activeRole = '', isSuperAdmin = false, hasCapability = () => false } = {}) {
  // Super admin always gets in (unless governance, which has its own guard)
  if (isSuperAdmin) return true;

  // Super admin only routes — deny everyone else
  if (route.superAdminOnly) return false;

  // ── DEFENSIVE: empty userRoles = deny ALL restricted routes ──
  // If session is broken / token has no roles, only public routes pass.
  // This prevents accidental access when RBAC data fails to load.
  if ((!userRoles || userRoles.length === 0) && route.rolesAllowed && route.rolesAllowed.length > 0) {
    return false;
  }

  // Capability check
  if (route.capability && hasCapability(route.capability)) return true;
  if (route.capability && !hasCapability(route.capability)) {
    // If capability is the ONLY requirement (rolesAllowed empty), deny
    if (!route.rolesAllowed || route.rolesAllowed.length === 0) return false;
  }

  // Public-auth routes (empty rolesAllowed)
  if (!route.rolesAllowed || route.rolesAllowed.length === 0) return true;

  // ── Role check — userRoles is the ONLY source of authority ──
  const normalUserRoles = userRoles.map(r => (r || '').toUpperCase());
  const hasTokenRole = route.rolesAllowed.some(r => normalUserRoles.includes(r));

  // Token must grant the role — activeRole alone is NOT enough
  if (!hasTokenRole) return false;

  // ── UX narrowing — activeRole can only RESTRICT, never grant ──
  // If user chose a specific profile, only show routes for THAT profile.
  // This prevents a TREINADOR+GESTOR user from seeing /manager while
  // switched to the TREINADOR profile (less confusing navigation).
  if (activeRole) {
    const normalActive = activeRole.toUpperCase();
    if (!route.rolesAllowed.includes(normalActive)) return false;
  }

  return true;
}

/**
 * Look up the RouteEntry for a given path.
 * Used for diagnostic/logging.
 */
export function findRoute(path) {
  return ROUTE_REGISTRY.find(r => r.path === path);
}
